// src/features/dashboard/teacher/MarksEntry.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  fetchAssessmentsForClass,
  fetchMarksForAssessment,
  saveMark,
  fetchTopicOptions,
} from '../../../services/sheetDataService';

const MarksEntry = ({ classItem, students, spreadsheetId }) => {
  const { accessToken, user } = useAuth();

  // Get the correct sheet IDs
  const sheetId = spreadsheetId || user.spreadsheetId;
  const topicsSheetId = user.topicsSpreadsheetId || user.spreadsheetId; // ✅ For topics

  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [marks, setMarks] = useState({});
  const [originalMarks, setOriginalMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState('');
  const [newName, setNewName] = useState('');
  const [newTerm, setNewTerm] = useState('');
  const [newDate, setNewDate] = useState('');

  // ✅ Topic options for the dropdown
  const [topicOptions, setTopicOptions] = useState([]);

  // Load assessments
  useEffect(() => {
    const loadAssessments = async () => {
      if (!accessToken || !sheetId || !classItem) return;
      const teacher = classItem.email || user.email;
      const subject = classItem.subject;
      const grade = classItem.grade;
      const asmts = await fetchAssessmentsForClass(
        accessToken,
        sheetId,
        teacher,
        subject,
        grade
      );
      setAssessments(asmts);
      if (asmts.length > 0) setSelectedAssessment(asmts[0]);
    };
    loadAssessments();
  }, [accessToken, sheetId, classItem, user.email]);

  // ✅ Fetch topics using the topics sheet ID
  useEffect(() => {
    const loadTopics = async () => {
      if (!accessToken || !topicsSheetId || !classItem) return;
      try {
        const teacher = classItem.email || user.email;
        const subject = classItem.subject;
        const grade = classItem.grade;
        const topics = await fetchTopicOptions(
          accessToken,
          topicsSheetId, // ✅ Use topicsSheetId
          teacher,
          subject,
          grade
        );
        setTopicOptions(topics);
      } catch (err) {
        console.error('Error loading topics:', err);
      }
    };
    loadTopics();
  }, [accessToken, topicsSheetId, classItem, user.email]);

  // Load marks for selected assessment
  useEffect(() => {
    const loadMarks = async () => {
      if (!selectedAssessment || !accessToken || !sheetId || !classItem) return;
      const teacher = classItem.email || user.email;
      const subject = classItem.subject;
      const grade = classItem.grade;
      const data = await fetchMarksForAssessment(
        accessToken,
        sheetId,
        teacher,
        subject,
        grade,
        selectedAssessment.type,
        selectedAssessment.name
      );
      const map = {};
      const origMap = {};
      data.forEach(item => {
        map[item.userId] = item.marks;
        origMap[item.userId] = item.marks;
      });
      setMarks(map);
      setOriginalMarks(origMap);
    };
    loadMarks();
  }, [selectedAssessment, accessToken, sheetId, classItem, user.email]);

  const handleScoreChange = (userId, value) => {
    setMarks(prev => ({ ...prev, [userId]: parseFloat(value) || 0 }));
  };

  const saveMarks = async () => {
    if (!selectedAssessment) {
      alert('Please select an assessment.');
      return;
    }
    if (!students || students.length === 0) {
      alert('No students to save marks for.');
      return;
    }

    const teacher = classItem.email || user.email;
    const subject = classItem.subject;
    const grade = classItem.grade;
    const { type, name, term, date: assessmentDate } = selectedAssessment;

    const changedRecords = [];
    students.forEach(student => {
      const newMark = marks[student.userId] !== undefined ? marks[student.userId] : 0;
      const oldMark = originalMarks[student.userId] !== undefined ? originalMarks[student.userId] : 0;
      if (newMark !== oldMark) {
        changedRecords.push({
          student,
          newMark,
          oldMark
        });
      }
    });

    if (changedRecords.length === 0) {
      alert('No changes to save.');
      return;
    }

    const changeList = changedRecords
      .map(r => `${r.student.name} ${r.student.surname}: ${r.oldMark} → ${r.newMark}`)
      .join('\n');
    if (!window.confirm(`You are about to update marks for:\n\n${changeList}\n\nProceed?`)) {
      return;
    }

    setSaving(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      const recordsToSave = changedRecords.map(({ student, newMark }) => ({
        userId: student.userId,
        organisationId: user.organisationId || '',
        school: user.school || 'N/A',
        province: user.province || 'N/A',
        district: user.district || 'N/A',
        circuit: user.circuit || 'N/A',
        teacher: teacher,
        grade: grade,
        subject: subject,
        type: type,
        name: name,
        term: term,
        assessmentDate: assessmentDate || '',
        studentName: student.name,
        studentSurname: student.surname,
        marks: newMark,
      }));

      for (const record of recordsToSave) {
        try {
          await saveMark(accessToken, sheetId, record);
          successCount++;
        } catch (err) {
          failCount++;
          errors.push(`Failed for ${record.studentName} ${record.studentSurname}: ${err.message}`);
          console.error('Mark save error:', err, record);
        }
      }

      const newOrigMap = { ...originalMarks };
      changedRecords.forEach(({ student, newMark }) => {
        newOrigMap[student.userId] = newMark;
      });
      setOriginalMarks(newOrigMap);

      let message = `✅ Marks saved: ${successCount} successful, ${failCount} failed.`;
      if (failCount > 0) {
        message += `\n\nErrors:\n${errors.join('\n')}`;
      }
      alert(message);
    } catch (err) {
      console.error('Save error:', err);
      alert('❌ Failed to save marks: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const createNewAssessment = async () => {
    if (!newType || !newName || !newTerm || !newDate) {
      alert('Please fill all fields (Type, Name, Term, Date).');
      return;
    }

    const newAssmt = {
      type: newType,
      name: newName,
      term: newTerm,
      date: newDate,
    };

    const exists = assessments.some(a =>
      a.type === newType &&
      a.name === newName &&
      a.term === newTerm &&
      a.date === newDate
    );

    if (exists) {
      alert('Assessment already exists.');
      return;
    }

    setAssessments([...assessments, newAssmt]);
    setSelectedAssessment(newAssmt);
    setNewType('');
    setNewName('');
    setNewTerm('');
    setNewDate('');
  };

  if (!selectedAssessment) {
    return (
      <div>
        <p>No assessments yet. Create one below.</p>
        <div className="mt-4 p-4 border rounded">
          <h4 className="font-semibold">Create New Assessment</h4>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-sm font-medium">Assessment Type</label>
              <input

                type="text"
                placeholder="Type (e.g., Test, Exam)"
                value={newType}
                onChange={e => setNewType(e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter Assessment Type 
              </p>
            </div>


            {/* Assessment Name with Topic dropdown (datalist) */}
            <div>
              <label className="block text-sm font-medium">Assessment Name</label>
              <input
                type="text"
                list="topic-options-marks"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Select a topic or type custom name"
                className="w-full border rounded px-2 py-1"
              />
              <datalist id="topic-options-marks">
                {topicOptions.map((opt, idx) => (
                  <option key={idx} value={opt.value} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500 mt-1">
                Type to search topics or enter a custom name
              </p>
            </div>

            <input
              type="text"
              placeholder="Term (e.g., Term 1)"
              value={newTerm}
              onChange={e => setNewTerm(e.target.value)}
              className="border p-1 rounded"
            />
            <input
              type="date"
              placeholder="Assessment Date"
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              className="border p-1 rounded"
            />
          </div>
          <button
            onClick={createNewAssessment}
            className="mt-2 bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            Create Assessment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {selectedAssessment.type} - {selectedAssessment.name} ({selectedAssessment.term})
          {selectedAssessment.date && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              Date: {new Date(selectedAssessment.date).toLocaleDateString()}
            </span>
          )}
        </h3>
        <button
          onClick={saveMarks}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Marks'}
        </button>
      </div>

      <table className="w-full table-auto border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2 text-left">Student</th>
            <th className="px-4 py-2 text-left">Score</th>
          </tr>
        </thead>
        <tbody>
          {students.map(s => {
            const isChanged = marks[s.userId] !== originalMarks[s.userId];
            return (
              <tr key={s.userId} className="border-t">
                <td className="px-4 py-2">{s.name} {s.surname}</td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    value={marks[s.userId] !== undefined ? marks[s.userId] : ''}
                    onChange={e => handleScoreChange(s.userId, e.target.value)}
                    className={`border p-1 rounded w-20 ${isChanged ? 'border-yellow-400 bg-yellow-50' : ''}`}
                    step="0.5"
                  />
                  {isChanged && (
                    <span className="ml-1 text-xs text-gray-500" title="Unsaved change">*</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex gap-2">
        <select
          value={`${selectedAssessment.type}|${selectedAssessment.name}|${selectedAssessment.term}|${selectedAssessment.date || ''}`}
          onChange={e => {
            const [type, name, term, date] = e.target.value.split('|');
            const asmt = assessments.find(a =>
              a.type === type &&
              a.name === name &&
              a.term === term &&
              (a.date || '') === date
            );
            setSelectedAssessment(asmt);
          }}
          className="border p-1 rounded"
        >
          {assessments.map(a => (
            <option
              key={`${a.type}|${a.name}|${a.term}|${a.date || ''}`}
              value={`${a.type}|${a.name}|${a.term}|${a.date || ''}`}
            >
              {a.type} - {a.name} ({a.term})
              {a.date ? ` (${new Date(a.date).toLocaleDateString()})` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={() => setSelectedAssessment(null)}
          className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
        >
          + New
        </button>
      </div>
    </div>
  );
};

export default MarksEntry;